import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetLifecycleQuery,
  useUpdateLifecycleMutation,
} from '@/redux/internal-services/onboardingApi';

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  Active: ['Suspended', 'Deactivated'],
  Suspended: ['Active', 'Deactivated'],
  Deactivated: ['Active'],
};

const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-green-100 text-green-800',
  Suspended: 'bg-yellow-100 text-yellow-800',
  Deactivated: 'bg-red-100 text-red-800',
};

const TenantLifecyclePage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useGetLifecycleQuery();
  const [updateLifecycle, { isLoading: saving }] = useUpdateLifecycleMutation();

  const lifecycle = data?.data;
  const currentStatus: string = lifecycle?.lifecycle_status ?? 'Active';
  const history: Array<{ from: string; to: string; reason: string; timestamp: string }> =
    (lifecycle?.lifecycle_history as Array<{ from: string; to: string; reason: string; timestamp: string }>) ?? [];

  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const allowedNext = ALLOWED_TRANSITIONS[currentStatus] ?? [];

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newStatus) { setError('Select a target status.'); return; }
    if (!reason.trim()) { setError('Reason is required.'); return; }
    try {
      await updateLifecycle({ status: newStatus, reason: reason.trim() }).unwrap();
      setSuccess(t('common.success'));
      setNewStatus('');
      setReason('');
      refetch();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-8">
      <h1 className="text-2xl font-bold mb-6">{t('settings.lifecycle.title')}</h1>

      <div className="bg-ui-card border border-ui-border rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-medium text-ui-text2">Current Status:</span>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[currentStatus] ?? 'bg-ui-subtle text-ui-text'}`}>
            {currentStatus}
          </span>
        </div>


        {allowedNext.length > 0 ? (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1">Transition To</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full border border-ui-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select status...</option>
                {allowedNext.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1">Reason *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full border border-ui-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Required — explain the reason for this transition"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {success && <p className="text-green-600 text-sm">{success}</p>}
            <button
              type="submit"
              disabled={saving}
              className="bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 transition disabled:opacity-50"
            >
              {saving ? t('common.loading') : 'Apply Transition'}
            </button>
          </form>
        ) : (
          <p className="text-ui-text2 text-sm">{t('admin.noTransitionsAvailable')}</p>
        )}
      </div>

      {history.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">History</h2>
          <div className="space-y-2">
            {[...history].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-3 bg-ui-raised rounded-lg p-3 text-sm">
                <div className="flex-1">
                  <span className="font-medium">{entry.from}</span>
                  <span className="mx-2 text-ui-text3">→</span>
                  <span className="font-medium">{entry.to}</span>
                  {entry.reason && <p className="text-ui-text2 mt-1">{entry.reason}</p>}
                </div>
                <span className="text-ui-text3 whitespace-nowrap">
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantLifecyclePage;
